export const VISIBLE_WEEK_DAYS = [
    { weekday: 1, label: 'Lunes' },
    { weekday: 2, label: 'Martes' },
    { weekday: 3, label: 'Miércoles' },
    { weekday: 4, label: 'Jueves' },
    { weekday: 5, label: 'Viernes' },
    { weekday: 6, label: 'Sábado' }
];

const SPANISH_LOCALE = 'es-PA';
const PANAMA_TIME_ZONE = 'America/Panama';

export const startOfLocalDay = (date = new Date()) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date, amount) => {
    const next = startOfLocalDay(date);
    next.setDate(next.getDate() + amount);
    return next;
};

export const addWeeks = (date, amount) => addDays(date, amount * 7);

export const getWeekMonday = (date) => {
    const localDate = startOfLocalDay(date);
    const day = localDate.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    return addDays(localDate, diff);
};

const getConfiguredWeekdays = (settings) =>
    settings?.planning?.commercialWeekdays || VISIBLE_WEEK_DAYS.map((day) => day.weekday);

export const getVisibleWeekDays = (date, settings) => {
    const monday = getWeekMonday(date);
    return getConfiguredWeekdays(settings)
        .map((weekday) => VISIBLE_WEEK_DAYS.find((day) => day.weekday === weekday))
        .filter(Boolean)
        .map((day) => ({
            ...day,
            date: addDays(monday, day.weekday - 1)
        }));
};

export const getPanamaToday = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: PANAMA_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    })
        .formatToParts(new Date())
        .reduce((acc, part) => {
            if (part.type !== 'literal') acc[part.type] = part.value;
            return acc;
        }, {});

    return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
};

export const getPanamaTodayKey = () => getLocalDateKey(getPanamaToday());

export const getPanamaWeekday = () => {
    const weekday = new Intl.DateTimeFormat('en-US', {
        timeZone: PANAMA_TIME_ZONE,
        weekday: 'short'
    }).format(new Date());
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday);
};

export const getInitialAgendaDate = (settings) => {
    const today = getPanamaToday();
    const weekday = getPanamaWeekday() || 7;
    const planningWeekdays = settings?.planning?.planningWeekdays || [5, 6];
    const deadlineWeekday = settings?.planning?.submissionDeadlineWeekday || 6;
    return planningWeekdays.includes(weekday) || weekday > deadlineWeekday
        ? addWeeks(today, settings?.planning?.weeksAhead || 1)
        : today;
};

export const isToday = (date) => startOfLocalDay(date).getTime() === startOfLocalDay(getPanamaToday()).getTime();

export const getLocalDateKey = (date) => {
    const localDate = startOfLocalDay(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const formatFullDate = (date) =>
    startOfLocalDay(date).toLocaleDateString(SPANISH_LOCALE, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });

export const formatDayNumber = (date) => startOfLocalDay(date).toLocaleDateString(SPANISH_LOCALE, { day: 'numeric' });

export const formatWeekRange = (date, settings) => {
    const days = getVisibleWeekDays(date, settings);
    const start = days[0].date;
    const end = days[days.length - 1].date;
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const sameYear = start.getFullYear() === end.getFullYear();

    if (sameMonth) {
        const monthYear = end.toLocaleDateString(SPANISH_LOCALE, { month: 'long', year: 'numeric' });
        return `${start.getDate()}–${end.getDate()} de ${monthYear}`;
    }

    if (sameYear) {
        const startLabel = start.toLocaleDateString(SPANISH_LOCALE, { day: 'numeric', month: 'long' });
        const endLabel = end.toLocaleDateString(SPANISH_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
        return `${startLabel}–${endLabel}`;
    }

    const startLabel = start.toLocaleDateString(SPANISH_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
    const endLabel = end.toLocaleDateString(SPANISH_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
    return `${startLabel}–${endLabel}`;
};

export const formatDateOnlyLong = (dateKey) => {
    if (!dateKey) return '';
    const [year, month, day] = String(dateKey).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(SPANISH_LOCALE, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
};

export const parseDateOnly = (dateKey) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || ''))) return null;
    const [year, month, day] = String(dateKey).split('-').map(Number);
    return new Date(year, month - 1, day);
};

export const formatDateTimePanama = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString(SPANISH_LOCALE, {
        timeZone: PANAMA_TIME_ZONE,
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
};

export const getWeekStartKey = (date) => getLocalDateKey(getWeekMonday(date));

export const getWeekEndKey = (date, settings) => {
    const weekdays = getConfiguredWeekdays(settings);
    return getLocalDateKey(addDays(getWeekMonday(date), Math.max(...weekdays) - 1));
};

export const isPastWeek = (date) => getWeekStartKey(date) < getWeekStartKey(getPanamaToday());

export const isCurrentWeek = (date) => getWeekStartKey(date) === getWeekStartKey(getPanamaToday());

export const isFutureWeek = (date) =>
    getWeekStartKey(date) > getWeekStartKey(getPanamaToday());

export const isNextPlanningWeek = (date, settings) =>
    getWeekStartKey(date) === getWeekStartKey(
        addWeeks(getPanamaToday(), settings?.planning?.weeksAhead || 1)
    );

export const isPlanningWindowOpen = (settings) => {
    const weekday = getPanamaWeekday() || 7;
    const planning = settings?.planning;
    if (!(planning?.planningWeekdays || [5, 6]).includes(weekday)) return false;
    if (!planning?.submissionDeadlineTime) return true;
    if (weekday < Number(planning.submissionDeadlineWeekday)) return true;
    if (weekday > Number(planning.submissionDeadlineWeekday)) return false;
    const [hours, minutes] = planning.submissionDeadlineTime.split(':').map(Number);
    const panamaTime = new Intl.DateTimeFormat('en-US', {
        timeZone: PANAMA_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
    })
        .formatToParts(new Date())
        .reduce((parts, part) => {
            if (part.type !== 'literal') parts[part.type] = part.value;
            return parts;
        }, {});
    return (Number(panamaTime.hour) * 60) + Number(panamaTime.minute) <= (hours * 60) + minutes;
};

export const isCommercialActivityDay = (settings) =>
    (settings?.planning?.commercialWeekdays || [1, 2, 3, 4, 5, 6])
        .includes(getPanamaWeekday() || 7);

export const getReportEffectiveDateKey = (visit = {}) => visit?.executionDate || visit?.plannedDate || null;

export const getReportWeekLockDate = (visit = {}, settings) => {
    const effectiveDate = parseDateOnly(getReportEffectiveDateKey(visit));
    if (!effectiveDate) return null;
    const reportSettings = settings?.reports || {};
    const lockDateKey = getLocalDateKey(
        addDays(getWeekMonday(effectiveDate), (reportSettings.lockWeekday || 8) - 1)
    );
    return new Date(`${lockDateKey}T${reportSettings.lockTime || '00:00'}:00-05:00`);
};

export const isReportWeekLocked = (visit = {}, settings) => {
    const lockDate = getReportWeekLockDate(visit, settings);
    if (!lockDate) return true;
    return Date.now() >= lockDate.getTime();
};

export const formatTimePanama = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString(SPANISH_LOCALE, {
        timeZone: PANAMA_TIME_ZONE,
        hour: 'numeric',
        minute: '2-digit'
    });
};

export const formatDuration = (seconds = 0) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainingSeconds = safeSeconds % 60;
    if (hours > 0) return `${hours} h ${String(minutes).padStart(2, '0')} min`;
    if (minutes > 0) return `${minutes} min ${String(remainingSeconds).padStart(2, '0')} s`;
    return `${remainingSeconds} s`;
};

export const getVisitSortValue = (visit = {}) => `${visit.plannedDate || ''}|${visit.plannedStartTime || '99:99'}|${String(visit.plannedOrder ?? 0).padStart(6, '0')}|${visit.createdAt || ''}`;

export const sortVisits = (visits = []) => [...visits].sort((left, right) => getVisitSortValue(left).localeCompare(getVisitSortValue(right)));

export const getVisitsForDate = (visits = [], date) => {
    const selectedKey = getLocalDateKey(date);
    return sortVisits(visits).filter((visit) => {
        const visitDate = visit?.date || visit?.scheduledDate || visit?.plannedDate;
        const parsedVisitDate = parseDateOnly(visitDate);
        return parsedVisitDate ? getLocalDateKey(parsedVisitDate) === selectedKey : false;
    });
};
